import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from 'recharts';

const mon = (k) => new Date(`${k}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'short' });
const gbp = (n) => `£${Number(n || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
const prettyTrade = (t) => (t || 'other').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const BRAND = 'rgb(var(--tm-brand-rgb))';
const tip = { contentStyle: { borderRadius: 10, border: '1px solid #e6e1d6', fontSize: 12 } };

function Card({ title, sub, children, tid }) {
  return (
    <section className="tm-card p-5" data-testid={tid}>
      <h2 className="font-display text-lg font-semibold">{title}</h2>
      <p className="text-xs text-slate-500 mb-3">{sub}</p>
      <div className="h-44">{children}</div>
    </section>
  );
}

export default function DashboardCharts({ charts }) {
  if (!charts) return null;
  const money = charts.money_in.map((m) => ({ ...m, label: mon(m.month) }));
  const win = charts.win_rate.map((m) => ({ ...m, label: mon(m.month) }));
  const total = money.reduce((a, m) => a + m.value, 0);
  const decided = win.reduce((a, m) => a + m.decided, 0), won = win.reduce((a, m) => a + m.won, 0);
  return (
    <div className="grid lg:grid-cols-3 gap-6 mt-8" data-testid="ws-charts">
      <Card tid="ws-chart-money" title="Money in" sub={`${gbp(total)} received in the last 6 months`}>
        {!total ? <p className="text-sm text-slate-500 pt-6">Record invoice payments and your monthly takings will chart here.</p> : (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={money} margin={{ top: 4, right: 4, left: -12, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="#eee8dc" />
            <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={11} />
            <YAxis tickLine={false} axisLine={false} fontSize={11} tickFormatter={(v) => (v >= 1000 ? `£${Math.round(v / 1000)}k` : `£${v}`)} />
            <Tooltip {...tip} formatter={(v) => [gbp(v), 'Received']} />
            <Bar dataKey="value" fill={BRAND} radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        )}
      </Card>
      <Card tid="ws-chart-winrate" title="Quote win rate" sub={decided ? `${Math.round((100 * won) / decided)}% overall · ${won} won of ${decided} decided` : 'No accepted or declined quotes yet'}>
        {!decided ? <p className="text-sm text-slate-500 pt-6">Once customers accept or decline quotes, your monthly win rate will show here.</p> : (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={win} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="#eee8dc" />
            <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={11} />
            <YAxis domain={[0, 100]} tickLine={false} axisLine={false} fontSize={11} tickFormatter={(v) => `${v}%`} />
            <Tooltip {...tip} formatter={(v, n, p) => [v == null ? '—' : `${v}% (${p.payload.won}/${p.payload.decided})`, 'Win rate']} />
            <Line type="monotone" dataKey="rate" stroke="#0e1a2b" strokeWidth={2.5} dot={{ r: 3, fill: BRAND, stroke: BRAND }} connectNulls />
          </LineChart>
        </ResponsiveContainer>
        )}
      </Card>
      <Card tid="ws-chart-trades" title="Best-paying job types" sub="Total value of won quotes by trade">
        {!charts.top_trades.length ? <p className="text-sm text-slate-500 pt-6">Win a few quotes and your best job types will show here.</p> : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart layout="vertical" data={charts.top_trades.map((t) => ({ ...t, label: prettyTrade(t.trade) }))} margin={{ top: 0, right: 8, left: 0, bottom: 0 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="label" width={110} tickLine={false} axisLine={false} fontSize={11} />
              <Tooltip {...tip} formatter={(v, n, p) => [`${gbp(v)} · ${p.payload.count} jobs · avg ${gbp(p.payload.avg)}`, 'Won']} />
              <Bar dataKey="value" fill={BRAND} radius={[0, 6, 6, 0]} barSize={14} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>
    </div>
  );
}
